/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useMemo, useRef } from "react";
import { debounce } from "@huub-web/utils";

/**
 * Hook that returns a memoized debounced version of the provided callback.
 * The debounced function persists across renders and will not be recreated.
 *
 * @param callback - The function to debounce
 * @param delay - The number of milliseconds to delay. Defaults to 200ms
 * @returns A memoized debounced function
 *
 * @example
 * ```tsx
 * const handleSearch = useDebounce((value: string) => {
 *   console.log("Searching for:", value);
 * }, 500);
 * ```
 */
export function useDebounce<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 200,
): (...args: Parameters<T>) => void {
  const callbackRef = useRef(callback);

  // Update callback ref when callback changes
  callbackRef.current = callback;

  // Memoize the debounced function
  return useMemo(
    () =>
      debounce((...args: Parameters<T>) => callbackRef.current(...args), delay),
    [delay],
  );
}
